import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Crown, Loader2, Calendar, RefreshCw, XCircle, ArrowLeftRight } from "lucide-react";
import { getSubscription, cancelSubscription, resumeSubscription, switchPlan } from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return "—"; }
}

export default function ManagePlanDialog({ open, onOpenChange, onChanged }) {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { setSub(await getSubscription()); } catch { setSub({}); } };
  useEffect(() => { if (open) { setSub(null); load(); } }, [open]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { const s = await fn(); setSub((prev) => ({ ...prev, ...s })); toast.success(msg); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed."); }
    finally { setBusy(false); }
  };

  const plan = sub?.plan || "monthly";
  const otherPlan = plan === "yearly" ? "monthly" : "yearly";
  const hasBilling = sub?.has_billing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manage-plan-dialog" className="max-w-md bg-[#0D0B18] border border-purple-500/30 text-white">
        <DialogTitle className="sr-only">Manage plan</DialogTitle>
        <DialogDescription className="sr-only">View renewal, switch plan, or cancel.</DialogDescription>
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-gradient-to-br from-amber-400/20 to-purple-600/20 border border-amber-300/30">
            <Crown className="w-5 h-5 text-amber-300" />
          </div>
          <h3 className="font-display text-xl font-bold text-white">Manage Premium</h3>
        </div>

        {!sub ? (
          <div className="flex justify-center py-8"><Loader2 className="w-7 h-7 animate-spin text-purple-300" /></div>
        ) : (
          <>
            <div className="rounded-xl bg-[#07070E] border border-purple-500/20 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-200/70">Current plan</span>
                <span data-testid="manage-current-plan" className="font-semibold text-white capitalize">Premium · {plan}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-200/70 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Renews</span>
                <span data-testid="manage-renewal-date" className="text-sm text-purple-100">{fmtDate(sub.current_period_end)}</span>
              </div>
              {sub.cancel_at_period_end && (
                <p data-testid="manage-cancel-note" className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/20 rounded-lg px-2 py-1.5">
                  Your plan is set to cancel at the end of the period. You'll keep Premium until then.
                </p>
              )}
            </div>

            {!hasBilling && (
              <p className="text-xs text-purple-300/60">
                This account has Premium enabled without an active Stripe billing subscription (test/comp account), so billing changes are unavailable.
              </p>
            )}

            <div className="space-y-2">
              <Button data-testid="manage-switch-plan-btn" disabled={busy || !hasBilling}
                onClick={() => run(() => switchPlan(otherPlan), `Switched to ${otherPlan} billing`)}
                variant="outline" className="w-full rounded-xl bg-transparent border-purple-500/30 text-purple-100 hover:bg-purple-500/10">
                <ArrowLeftRight className="w-4 h-4 mr-2" /> Switch to {otherPlan === "yearly" ? "Yearly ($124.99/yr)" : "Monthly ($12.99/mo)"}
              </Button>

              {sub.cancel_at_period_end ? (
                <Button data-testid="manage-resume-btn" disabled={busy || !hasBilling}
                  onClick={() => run(resumeSubscription, "Subscription resumed")}
                  className="w-full rounded-xl bg-purple-600 hover:bg-purple-500 text-white">
                  <RefreshCw className="w-4 h-4 mr-2" /> Resume subscription
                </Button>
              ) : (
                <Button data-testid="manage-cancel-btn" disabled={busy || !hasBilling}
                  onClick={() => run(cancelSubscription, "Cancellation scheduled for period end")}
                  variant="outline" className="w-full rounded-xl bg-transparent border-rose-500/30 text-rose-300 hover:bg-rose-500/10">
                  <XCircle className="w-4 h-4 mr-2" /> Cancel subscription
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
